import React, { ReactNode } from 'react';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import PortfolioContext from './context';
import { usePortfolioDetails } from '../hooks/usePortfolioDetails';
import { useModelPortfolio } from '../hooks/useModelPortfolio';
import { useLastApplication } from '../hooks/useLastApplication';
import { useApplication } from '../hooks/useApplication';
import { useProposal } from '../hooks/useProposal';

// --- Types ---

interface PortfolioOptions {
    id: number;
    clientId: string | number;
    portfolio?: any; // Replace 'any' with your specific hook option types
    modelPortfolio?: any;
    lastApplication?: any;
    application?: any;
    proposal?: any;
}

interface PortfolioOverviewProviderProps {
    options: PortfolioOptions;
    children: ReactNode;
}

// This interface represents the 'state' object
export interface PortfolioState {
    id: number;
    clientId: string | number;
    portfolio: ReturnType<typeof usePortfolioDetails>;
    modelPortfolio: ReturnType<typeof useModelPortfolio>;
    lastApplication: ReturnType<typeof useLastApplication>;
    application: ReturnType<typeof useApplication>;
    proposal: ReturnType<typeof useProposal>;
    generalAgency: any;
}

// --- Component ---

const PortfolioOverviewProvider: React.FC<PortfolioOverviewProviderProps> = ({
    options,
    children,
}) => {
    const {
        id,
        clientId,
        portfolio: portfolioOptions,
        modelPortfolio: modelPortfolioOptions,
        lastApplication: lastApplicationOptions,
        application: applicationOptions,
        proposal: proposalOptions,
    } = options;

    // Hook calls
    const modelPortfolio = useModelPortfolio({ ...modelPortfolioOptions, loadInitially: true });
    const portfolio = usePortfolioDetails(
        { ...portfolioOptions, clientId, portfolioId: id },
        modelPortfolio,
        true, // loadInitially
    );
    const lastApplication = useLastApplication({ ...lastApplicationOptions, loadInitially: true });
    const application = useApplication({ ...applicationOptions, loadInitially: true });
    const proposal = useProposal({ ...proposalOptions, loadInitially: true });

    const generalAgencyData = useGetCustomService({
        service: 'planService',
        api: 'getAgentAndAgencyInfo',
        payload: { portfolioId: id },
        enabled: !!id,
    });

    const generalAgency = {
        data: generalAgencyData?.data?.data ?? {},
        error: generalAgencyData?.error,
        isLoading: generalAgencyData?.isLoading,
        getAgentAndGeneralAgencyData: generalAgencyData?.refetch,
    };

    const state: PortfolioState = {
        id,
        clientId,
        portfolio,
        modelPortfolio,
        lastApplication,
        application,
        proposal,
        generalAgency,
    };

    // Callback type helper
    const getValue = <T, >(callback: (s: PortfolioState) => T): T => callback(state);

    return (
        <PortfolioContext.Provider value={{ ...state, getValue }}>
            {children}
        </PortfolioContext.Provider>
    );
};

export default PortfolioOverviewProvider;
